import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { ArrowLeft } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", handicap: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const first = form.firstName.trim();
    const last = form.lastName.trim();
    if (!first || !last) { setError("Please enter both your first and last name."); return; }
    setSubmitting(true);
    try {
      const { joinedLeagueId } = await register({
        name: `${first} ${last}`,
        email: form.email.trim(),
        password: form.password,
        handicap: form.handicap ? Number(form.handicap) : null,
      });
      if (joinedLeagueId) {
        nav(`/app/leagues/${joinedLeagueId}`, { replace: true });
      } else {
        nav("/app", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell min-h-screen p-6 flex flex-col">
      <Link to="/" data-testid="back-to-landing" className="btn-ghost self-start -ml-2">
        <ArrowLeft size={20} weight="bold" /> Back
      </Link>

      <div className="flex items-center mt-6 mb-2" data-testid="register-logo">
        <img src="/fairway-logo.png" alt="Fairway" className="h-16 w-auto rounded-2xl" />
      </div>

      <h1 className="font-heading font-black tracking-tighter-2 text-4xl sm:text-5xl text-[var(--primary)] mt-6 mb-2">
        Tee it up
      </h1>
      <p className="text-[var(--text-secondary)] mb-8">Create your account and start tracking rounds.</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-uppercase block mb-2">First name</label>
            <input
              value={form.firstName}
              onChange={update("firstName")}
              required
              autoComplete="given-name"
              className="input-field"
              placeholder="Jamie"
              data-testid="register-first-name-input"
            />
          </div>
          <div>
            <label className="label-uppercase block mb-2">Last name</label>
            <input
              value={form.lastName}
              onChange={update("lastName")}
              required
              autoComplete="family-name"
              className="input-field"
              placeholder="Smith"
              data-testid="register-last-name-input"
            />
          </div>
        </div>
        <div>
          <label className="label-uppercase block mb-2">Email</label>
          <input type="email" value={form.email} onChange={update("email")} required className="input-field" placeholder="you@example.com" data-testid="register-email-input" />
        </div>
        <div>
          <label className="label-uppercase block mb-2">Password</label>
          <input type="password" value={form.password} onChange={update("password")} required minLength={6} className="input-field" placeholder="At least 6 characters" data-testid="register-password-input" />
        </div>
        <div>
          <label className="label-uppercase block mb-2">Handicap (optional)</label>
          <input type="number" value={form.handicap} onChange={update("handicap")} className="input-field" placeholder="e.g. 14" data-testid="register-handicap-input" />
        </div>

        {error && (
          <div data-testid="register-error" className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary mt-2"
          disabled={submitting}
          data-testid="register-submit-button"
        >
          {submitting ? "Creating..." : "Create account"}
        </button>
      </form>

      <p className="text-center mt-8 text-[var(--text-secondary)]">
        Have an account? <Link to="/login" data-testid="link-to-login" className="text-[var(--primary)] font-semibold underline">Sign in</Link>
      </p>
    </div>
  );
}
